/**
 * Welle 1 (Cockpit-Intelligenz) W1-E — CockpitMessageComposer: pre-filled,
 * editable "Nachricht an Mandant" (Aktion 1c advisor side).
 *
 * Reference: architecture-cockpit-intelligence.md §Interfaces (FE —
 * Messaging-Textbausteine) + §State-Management #6 + modules-cockpit-
 * intelligence.md W1-E.
 *
 * ⚠️ KRITISCHE FALLE (Regression-Guard): the StB in the cockpit is
 * CROSS-TENANT — this component deliberately does NOT use
 * `useMessages`/`MessageContextButton`/`effectiveScopeUid` (those resolve to
 * the ADVISOR's own scope, which is the WRONG tenant here). It calls the
 * low-level `sendMessage` directly with an EXPLICIT `scopeUid: mandantUid` and
 * `authorRole: 'advisor'`. Send-only in Welle 1 (kein Inline-Verlauf, s.
 * architecture §Welle-2-Erweiterungspunkte Nr. 5).
 *
 * @module components/moat/CockpitMessageComposer
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconSend, IconClose } from '../../icons';
import { useAuth } from '../../context/AuthContext';
import { sendMessage } from '../../api/messagesApi';

export interface CockpitMessageComposerProps {
  /** The mandant (tenant owner) this message is posted INTO — explicit scope. */
  mandantUid: string;
  /** The `inbound_documents` doc id the message references. */
  docId: string;
  /** The finding's `code` — selects the i18n template (`cockpit.messageTemplate.<code>`). */
  code: string;
  invoiceNumber: string;
  supplierName: string;
  onClose: () => void;
}

const GENERIC_FALLBACK =
  'Zu Beleg {{invoiceNumber}} von {{supplier}} gibt es eine offene Rückfrage. Bitte prüfen.';

type SendStatus = { kind: 'idle' } | { kind: 'sending' } | { kind: 'sent' } | { kind: 'error' };

/**
 * Builds the pre-filled template text for a finding `code`, falling back to
 * `cockpit.messageTemplate.generic` when no code-specific Baustein exists
 * (architecture §Interfaces "Fallback (alle übrigen)").
 *
 * @param t the i18next translator (namespace `common`).
 * @param code the finding's machine code.
 * @param invoiceNumber the invoice number for interpolation (falls back to '').
 * @param supplier the supplier name for interpolation (falls back to '').
 * @returns the editable, pre-filled message text.
 */
function buildTemplateText(
  t: (key: string, opts?: Record<string, unknown>) => string,
  code: string,
  invoiceNumber: string,
  supplier: string,
): string {
  const vars = { invoiceNumber: invoiceNumber || '', supplier: supplier || '' };
  const specific = t(`cockpit.messageTemplate.${code}`, { ...vars, defaultValue: '' });
  if (specific) return specific;
  return t('cockpit.messageTemplate.generic', { ...vars, defaultValue: GENERIC_FALLBACK });
}

/**
 * Modal composer for the StB-Cockpit's "Nachricht an Mandant" action. Opens
 * pre-filled with a code-specific Baustein (editable before send), posts a
 * regular `receipt`-scoped message via `sendMessage` with an EXPLICIT scope
 * (never `effectiveScopeUid`), and shows local success/error state.
 *
 * @param props see {@link CockpitMessageComposerProps}.
 */
const CockpitMessageComposer: React.FC<CockpitMessageComposerProps> = ({
  mandantUid,
  docId,
  code,
  invoiceNumber,
  supplierName,
  onClose,
}) => {
  const { t } = useTranslation('common');
  const { currentUser } = useAuth();
  const [text, setText] = useState(() => buildTemplateText(t, code, invoiceNumber, supplierName));
  const [status, setStatus] = useState<SendStatus>({ kind: 'idle' });

  const canSend = text.trim().length > 0 && !!currentUser?.uid && status.kind !== 'sending';

  const handleSend = async (): Promise<void> => {
    if (!currentUser?.uid || text.trim().length === 0) return;
    setStatus({ kind: 'sending' });
    try {
      await sendMessage(
        {
          context: { type: 'receipt', refId: docId },
          refCollection: 'inbound_documents',
          text,
        },
        {
          // EXPLICIT cross-tenant scope — the fix for the "KRITISCHE FALLE"
          // above. NEVER effectiveScopeUid here.
          scopeUid: mandantUid,
          authorUid: currentUser.uid,
          authorRole: 'advisor',
        },
      );
      setStatus({ kind: 'sent' });
    } catch {
      setStatus({ kind: 'error' });
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('cockpit.message.title', 'Nachricht an Mandant')}
        className="fixed inset-x-4 top-1/2 z-50 mx-auto max-w-md -translate-y-1/2 rounded-lg border border-hairline bg-surface-raised p-4 shadow-xl sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2"
      >
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-primary">
            {t('cockpit.message.title', 'Nachricht an Mandant')}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('cockpit.message.close', 'Schließen')}
            className="text-muted hover:text-primary"
          >
            <IconClose size={16} />
          </button>
        </div>

        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={5}
          maxLength={4000}
          disabled={status.kind === 'sending' || status.kind === 'sent'}
          className="w-full rounded-md border border-strong bg-surface px-3 py-2 text-sm text-primary focus:border-indigo-400 focus:outline-none disabled:opacity-60"
        />

        <div className="mt-3 flex items-center justify-between">
          <div className="text-xs">
            {status.kind === 'sent' && (
              <span className="text-[var(--tone-success-fg)]">
                {t('cockpit.message.success', 'Nachricht gesendet.')}
              </span>
            )}
            {status.kind === 'error' && (
              <span className="text-[var(--tone-danger-fg)]">
                {t('cockpit.message.error', 'Die Nachricht konnte nicht gesendet werden.')}
              </span>
            )}
          </div>
          {status.kind === 'sent' ? (
            <button
              type="button"
              onClick={onClose}
              className="rounded-md bg-surface-inset px-4 py-2 text-sm font-semibold text-primary transition hover:bg-surface-hover"
            >
              {t('cockpit.message.close', 'Schließen')}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSend}
              disabled={!canSend}
              className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <IconSend size={12} />
              {status.kind === 'sending'
                ? t('cockpit.message.sending', 'Wird gesendet …')
                : t('cockpit.message.send', 'Senden')}
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default CockpitMessageComposer;
